import { useQuery } from '@tanstack/react-query';
import type { components } from '@sentinel/api-client';
import { useApi } from '../../lib/api';

/** The end-user product DTO from `GET /auth/products` (schema-derived, zero drift). */
export type AuthProduct = components['schemas']['AuthProductResponse'];

export interface LaunchProductsResult {
  items: AuthProduct[];
  total: number;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
}

/**
 * Fetch the authenticated user's entitled products for the post-login resolver
 * and the `/select-product` chooser. `limit: 100` keeps the count-based decision
 * (0 / 1 / >1) accurate within the first page. Gate with `enabled` so the request
 * never fires before the session is established.
 */
export function useLaunchProducts(enabled: boolean): LaunchProductsResult {
  const api = useApi();
  const query = useQuery({
    queryKey: ['launch-products'],
    enabled,
    queryFn: async () => {
      // limit:100 caps the chooser list. The 0/1/>1 routing decision uses the
      // server `total` (not items.length), so >100 entitled products still
      // routes to the chooser correctly; the chooser renders the first 100.
      // The product catalog is inherently small (single digits), so this cap
      // is a documented non-issue rather than a pagination feature.
      const { data, error } = await api.GET('/auth/products', {
        params: { query: { limit: 100 } },
      });
      if (error) throw error;
      return (data ?? { items: [], total: 0, next_cursor: null }) as {
        items: AuthProduct[];
        total: number;
        next_cursor: string | null;
      };
    },
  });

  return {
    items: query.data?.items ?? [],
    total: query.data?.total ?? 0,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => void query.refetch(),
  };
}
